import { useMemo, useState } from 'react';
import { personalSessionKey } from '../hooks/useGateway';
import { agentIdFromSessionKey, useAppStore } from '../stores/appStore';
import { useSettingsStore } from '../stores/settingsStore';
import { filterAgentsForAllowedList } from '../utils/agentVisibility';

interface SessionSidebarProps {
  onSelectSession: (sessionKey: string) => Promise<void> | void;
  refreshSessions: () => Promise<void>;
  onCollapse: () => void;
}

function sessionKeyFromSession(session: any): string {
  return session?.sessionKey ?? session?.key ?? session?.id ?? 'unknown-session';
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        width: '10px',
        height: '10px',
        transition: 'transform 0.2s ease',
        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
        flexShrink: 0,
      }}
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export default function SessionSidebar({
  onSelectSession,
  refreshSessions,
  onCollapse,
}: SessionSidebarProps) {
  const sessions = useAppStore((state) => state.sessions);
  const agents = useAppStore((state) => state.agents);
  const selectedAgentId = useAppStore((state) => state.selectedAgentId);
  const setSelectedAgentId = useAppStore((state) => state.setSelectedAgentId);
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const allowedAgents = useAppStore((state) => state.allowedAgents);
  const userRole = useAppStore((state) => state.userRole);
  const setCurrentView = useAppStore((state) => state.setCurrentView);
  const currentView = useAppStore((state) => state.currentView);
  const openSettings = useAppStore((state) => state.openSettings);
  const displayName = useSettingsStore((state) => state.settings.general.displayName);
  const updateGeneral = useSettingsStore((state) => state.updateGeneral);

  const [nameDraft, setNameDraft] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [privateOpen, setPrivateOpen] = useState(true);
  const [teamOpen, setTeamOpen] = useState(true);
  const [studioOpen, setStudioOpen] = useState(true);

  const trimmedDisplayName = displayName.trim();

  const pickableAgents = useMemo(() => {
    const AGENT_ORDER = ['main', 'coco', 'edge', 'yuki', 'spike', 'researcher', 'coder', 'btw'];
    const withIdentity = agents.filter((agent) => agent.identity != null);
    const filtered = filterAgentsForAllowedList(withIdentity, allowedAgents);
    return filtered.sort((a, b) => {
      const ai = AGENT_ORDER.indexOf(a.id);
      const bi = AGENT_ORDER.indexOf(b.id);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [agents, allowedAgents]);

  const pickerVisible = pickableAgents.length > 0;

  const activeAgentId = activeSessionKey ? agentIdFromSessionKey(activeSessionKey) : null;

  const effectiveSelectedAgentId = pickerVisible
    ? ((selectedAgentId && pickableAgents.some((a) => a.id === selectedAgentId) ? selectedAgentId : null)
      ?? (activeAgentId && pickableAgents.some((a) => a.id === activeAgentId) ? activeAgentId : null)
      ?? pickableAgents[0]?.id ?? null)
    : null;

  const normalized = useMemo(() => {
    const allSessions = sessions.map((session) => {
      const key = sessionKeyFromSession(session);
      // Title priority: explicit title from backend > user segment from key
      let label: string;
      if (session?.title) {
        label = session.title;
      } else {
        // Extract user segment (3rd colon-separated part), e.g. "jackie" from "agent:flint:jackie"
        // For archived keys like "agent:flint:jackie:uuid", still show "jackie"
        const parts = key.split(':');
        label = parts.length >= 3 ? parts[2] : key;
      }
      return {
        raw: session,
        key,
        label,
        fullKey: key, // for tooltip
        kind: session?.kind ?? 'session',
      };
    });

    if (!pickerVisible || !effectiveSelectedAgentId) {
      return allSessions;
    }

    return allSessions.filter((session) => {
      if (agentIdFromSessionKey(session.key) !== effectiveSelectedAgentId) {
        return false;
      }
      if (effectiveSelectedAgentId === 'yuki' && session.key === 'agent:yuki:family') {
        return false;
      }
      return true;
    });
  }, [sessions, pickerVisible, effectiveSelectedAgentId]);

  const displaySessions = useMemo(() => {
    const isMember = userRole === 'member';

    if (isMember && trimmedDisplayName && effectiveSelectedAgentId) {
      const personalKey = personalSessionKey(effectiveSelectedAgentId, trimmedDisplayName);
      const familyKey = effectiveSelectedAgentId === 'yuki' ? 'agent:yuki:family' : null;

      const visible = normalized.filter(
        (s) => s.key === personalKey || (familyKey != null && s.key === familyKey),
      );

      if (!visible.some((s) => s.key === personalKey)) {
        visible.push({
          raw: null as any,
          key: personalKey,
          label: trimmedDisplayName,
          fullKey: personalKey,
          kind: 'session',
        });
      }

      return visible;
    }

    if (effectiveSelectedAgentId !== 'yuki' || !trimmedDisplayName) {
      return normalized;
    }

    const personalKey = personalSessionKey('yuki', trimmedDisplayName);
    const familyKey = 'agent:yuki:family';

    const visible = normalized.filter(
      (s) => s.key === personalKey || s.key === familyKey,
    );

    if (!visible.some((s) => s.key === personalKey)) {
      visible.push({
        raw: null as any,
        key: personalKey,
        label: trimmedDisplayName,
        fullKey: personalKey,
        kind: 'session',
      });
    }

    return visible;
  }, [normalized, effectiveSelectedAgentId, trimmedDisplayName, userRole]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshSessions();
    } finally {
      setRefreshing(false);
    }
  };

  const handleAgentSelect = async (agentId: string) => {
    setSelectedAgentId(agentId);
    setCurrentView('chat');

    const currentDisplayName = useSettingsStore.getState().settings.general.displayName.trim();

    await onSelectSession(personalSessionKey(agentId, currentDisplayName));
  };

  const handleSessionClick = (sessionKey: string) => {
    setCurrentView('chat');
    setSelectedAgentId(agentIdFromSessionKey(sessionKey));
    void onSelectSession(sessionKey);
  };

  const handleNamePromptSubmit = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      return;
    }

    updateGeneral({ displayName: trimmed });
    setCurrentView('chat');
    const agentId = effectiveSelectedAgentId ?? 'yuki';
    await onSelectSession(personalSessionKey(agentId, trimmed));
    void refreshSessions().catch((error) => {
      console.warn('[SessionSidebar] Unable to refresh sessions after name submit:', error);
    });
  };

  const handleStudioAction = (view: 'dashboard' | 'assets' | 'settings' | 'notes') => {
    if (view === 'settings') {
      openSettings();
      return;
    }
    if (view === 'notes') {
      import('../stores/notesStore').then(({ useNotesStore }) => {
        useNotesStore.getState().togglePanel();
      });
      return;
    }
    if (view === 'assets') {
      import('../stores/fileEditorStore').then(({ useFileEditorStore }) => {
        useFileEditorStore.getState().openPanel();
      });
    }
    setCurrentView(view);
  };

  const showNamePrompt = effectiveSelectedAgentId === 'yuki' && !trimmedDisplayName;

  const studioItems = [
    {
      id: 'dashboard' as const,
      label: 'Dashboard',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', flexShrink: 0 }}>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
    },
    {
      id: 'assets' as const,
      label: 'Files',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', flexShrink: 0 }}>
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      ),
    },
    {
      id: 'notes' as const,
      label: 'Notes',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', flexShrink: 0 }}>
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      ),
    },
    {
      id: 'settings' as const,
      label: 'Settings',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', flexShrink: 0 }}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="sidebar-panel">
      {/* Wordmark + collapse */}
      <div className="sidebar-wordmark-row">
        <span className="sidebar-wordmark">Clawlet</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
            className="sidebar-icon-btn"
            title="Refresh sessions"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: '11px', height: '11px' }}>
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onCollapse}
            className="sidebar-icon-btn"
            title="Collapse sidebar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: '13px', height: '13px' }}>
              <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />
            </svg>
          </button>
        </div>
      </div>

      {/* Scrollable sections */}
      <div className="sidebar-scroll">

        {/* ── PRIVATE section ── */}
        <div className="sidebar-section">
          <button
            type="button"
            className="sidebar-section-header"
            onClick={() => setPrivateOpen((o) => !o)}
          >
            <ChevronIcon open={privateOpen} />
            <span>Private</span>
          </button>

          {privateOpen && (
            <div className="sidebar-section-body">
              {/* Agent picker */}
              {pickerVisible && (
                <div className="space-y-1 mb-2">
                  {pickableAgents.map((agent, idx) => {
                    const isActiveAgent = effectiveSelectedAgentId === agent.id;
                    const agentDisplayName = agent.identity?.name ?? agent.name ?? agent.id;
                    const emoji = agent.identity?.emoji ?? '';
                    const accentColors = ['var(--theme-primary)', '#3DFFA8', '#7B8FFF', '#FFD93D'];
                    const accentColor = accentColors[idx % accentColors.length];

                    return (
                      <button
                        key={agent.id}
                        type="button"
                        onClick={() => void handleAgentSelect(agent.id)}
                        className="sidebar-nav-item"
                        style={{
                          borderLeftColor: isActiveAgent ? accentColor : 'transparent',
                          background: isActiveAgent ? 'var(--theme-accent)' : 'transparent',
                          color: isActiveAgent ? accentColor : 'var(--theme-card-foreground)',
                        }}
                        title={agentDisplayName}
                      >
                        {emoji && <span style={{ fontSize: '0.85rem' }}>{emoji}</span>}
                        <span style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                          {agentDisplayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Name prompt for Yuki */}
              {showNamePrompt ? (
                <div className="rounded-lg border p-3 space-y-3" style={{ borderColor: 'var(--theme-border)', background: 'var(--theme-background)' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-foreground)' }}>What's your name?</p>
                    <p className="mt-1 text-xs" style={{ color: 'var(--theme-muted-foreground)' }}>
                      Yuki uses your name for private sessions.
                    </p>
                  </div>
                  <form
                    className="space-y-2"
                    onSubmit={(e) => { e.preventDefault(); void handleNamePromptSubmit(); }}
                  >
                    <input
                      type="text"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                      style={{ border: '1px solid var(--theme-border)', background: 'var(--theme-card)', color: 'var(--theme-foreground)' }}
                      placeholder="Your name"
                    />
                    <button
                      type="submit"
                      disabled={!nameDraft.trim()}
                      className="rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-60"
                      style={{ background: 'var(--theme-primary)', color: 'var(--theme-primary-foreground)' }}
                    >
                      Go
                    </button>
                  </form>
                </div>
              ) : (
                <div className="space-y-0.5">
                  {/* Family chat for Yuki */}
                  {effectiveSelectedAgentId === 'yuki' && (
                    <button
                      type="button"
                      onClick={() => handleSessionClick('agent:yuki:family')}
                      className="sidebar-nav-item"
                      style={{
                        borderLeftColor: activeSessionKey === 'agent:yuki:family' ? 'var(--theme-primary)' : 'transparent',
                        background: activeSessionKey === 'agent:yuki:family' ? 'var(--theme-accent)' : 'transparent',
                        color: activeSessionKey === 'agent:yuki:family' ? 'var(--theme-primary)' : 'var(--theme-card-foreground)',
                      }}
                    >
                      <span style={{ fontSize: '0.85rem' }}>👨‍👧‍👧</span>
                      <span className="text-sm truncate">Family Chat</span>
                    </button>
                  )}

                  {displaySessions.length === 0 ? (
                    <div className="px-3 py-3" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.65rem', color: 'var(--theme-muted-foreground)' }}>
                      No sessions
                    </div>
                  ) : (
                    displaySessions.map((session) => {
                      const active = session.key === activeSessionKey;
                      return (
                        <button
                          key={session.key}
                          type="button"
                          onClick={() => handleSessionClick(session.key)}
                          className="sidebar-nav-item"
                          style={{
                            borderLeftColor: active ? 'var(--theme-primary)' : 'transparent',
                            background: active ? 'var(--theme-accent)' : 'transparent',
                            color: active ? 'var(--theme-primary)' : 'var(--theme-card-foreground)',
                          }}
                          title={session.fullKey ?? session.key}
                        >
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-sm truncate" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.label}</span>
                            <span style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}>
                              {session.kind}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── TEAM section ── */}
        <div className="sidebar-section">
          <button
            type="button"
            className="sidebar-section-header"
            onClick={() => setTeamOpen((o) => !o)}
          >
            <ChevronIcon open={teamOpen} />
            <span>Team</span>
          </button>

          {teamOpen && (
            <div className="sidebar-section-body">
              <button
                type="button"
                onClick={() => setCurrentView('team')}
                className="sidebar-nav-item"
                style={{
                  borderLeftColor: currentView === 'team' ? 'var(--theme-primary)' : 'transparent',
                  background: currentView === 'team' ? 'var(--theme-accent)' : 'transparent',
                  color: currentView === 'team' ? 'var(--theme-primary)' : 'var(--theme-card-foreground)',
                }}
              >
                <span style={{ fontSize: '0.85rem' }}>💬</span>
                <span className="text-sm"># team</span>
              </button>
            </div>
          )}
        </div>

        {/* ── STUDIO section ── */}
        <div className="sidebar-section">
          <button
            type="button"
            className="sidebar-section-header"
            onClick={() => setStudioOpen((o) => !o)}
          >
            <ChevronIcon open={studioOpen} />
            <span>Studio</span>
          </button>

          {studioOpen && (
            <div className="sidebar-section-body">
              {studioItems.map((item) => {
                const isActive = item.id !== 'settings' && currentView === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleStudioAction(item.id)}
                    className="sidebar-nav-item"
                    style={{
                      borderLeftColor: isActive ? 'var(--theme-primary)' : 'transparent',
                      background: isActive ? 'var(--theme-accent)' : 'transparent',
                      color: isActive ? 'var(--theme-primary)' : 'var(--theme-card-foreground)',
                    }}
                  >
                    {item.icon}
                    <span className="text-sm">{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
